import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';
import moment from 'moment';

/**
 * Custom hook for Headcount Project Table auto-save functionality
 * 
 * This hook manages:
 * - Auto-save detection and restoration
 * - Polling for data changes every 500ms
 * - Manual update flag to prevent polling overwrites
 * - Pending deletes tracking
 * 
 */
export const useHeadcountAutoSave = (
    isEditing: boolean,
    headcountData: any[],
    HPRef: React.RefObject<any>,
    setHeadcountData: (data: any) => void,
    expandAllCategoriesWithChildren?: (data: any[]) => void
) => {
    // Auto-save state
    const [autoSaveVisible, setAutoSaveVisible] = useState(false);
    const [headcountEditData, setHeadcountEditData] = useState<any[]>([]);
    const [pendingDeletes, setPendingDeletes] = useState<any[]>([]);
    const isManualUpdateRef = useRef(false);

    // Auto-save: construct unique pageKey for Headcount Project Table
    const headcountPageKey = useMemo(() => {
        return EDIT_PAGE_KEY_MAP.publishing_headcount_project_table;
    }, []);

    // Auto-save: get methods (check if auto-save exists) - First call without dependencies
    const autoSaveMethods: any = useAutoSaveParams({
        pageKey: headcountPageKey,
    });

    const {
        getSaveParamsValue,
        showRestorePrompt,
        deleteSaveParamsByPageKey
    } = autoSaveMethods;

    // Track edit data changes for auto-save - Poll frequently to capture real-time edits
    useEffect(() => {
        if (isEditing && HPRef.current) {
            // Poll getEditData() frequently to capture latest edits (since TableContent doesn't expose onEditDataChange)
            // We poll every 500ms to ensure we capture changes before the 5-second auto-save
            const interval = setInterval(() => {
                // Skip polling if we just did a manual update (like deletion)
                if (isManualUpdateRef.current) {
                    isManualUpdateRef.current = false; // Reset flag
                    return;
                }

                const currentEditData = HPRef.current?.getEditData?.();
                if (currentEditData && Array.isArray(currentEditData)) {
                    // Only update if data actually changed (to avoid unnecessary re-renders)
                    setHeadcountEditData((prev: any[]) => {
                        const prevStr = JSON.stringify(prev);
                        const currentStr = JSON.stringify(currentEditData);
                        if (prevStr !== currentStr) {
                            return currentEditData;
                        }
                        return prev;
                    });
                }
            }, 500); // Check every 500ms for changes

            return () => clearInterval(interval);
        } else if (!isEditing) {
            // Reset when exiting edit mode
            setHeadcountEditData([]);
        }
    }, [isEditing, HPRef]);

    // Initialize edit data when entering edit mode
    useEffect(() => {
        if (isEditing && headcountData && headcountData.length > 0) {
            setHeadcountEditData(headcountData);
        }
    }, [isEditing]); // Only when isEditing changes

    // 🔥 CRITICAL: Only compute currentSaveParams when in edit mode to prevent race conditions
    // If we compute it when NOT editing, fresh API data can overwrite auto-saved edits
    const currentSaveParams = useMemo(() => {
        if (!isEditing) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                tableData: [],
                pendingDeletes: [],
            };
        }

        // When editing, use actual edit data
        return {
            tableData: headcountEditData.length > 0 ? headcountEditData : headcountData,
            pendingDeletes: pendingDeletes,
        };
    }, [isEditing, headcountEditData, headcountData, pendingDeletes]);

    // 🔥 CRITICAL: Memoize initialParams to capture the starting state when entering edit mode
    // This ensures auto-save detection works correctly by comparing against the original data
    const initialParamsRef = useRef<{
        tableData: any[];
        pendingDeletes: any[];
    }>({
        tableData: [],
        pendingDeletes: [],
    });

    // Update initialParams when entering edit mode (capture the original state)
    useEffect(() => {
        if (isEditing && headcountData.length > 0) {
            // Only update if initialParams is empty (first time entering edit mode)
            if (initialParamsRef.current.tableData.length === 0) {
                initialParamsRef.current = {
                    tableData: JSON.parse(JSON.stringify(headcountData)), // Deep clone to avoid reference issues
                    pendingDeletes: [],
                };
            }
        } else if (!isEditing) {
            // Reset initialParams when exiting edit mode (prepare for next edit session)
            initialParamsRef.current = {
                tableData: [],
                pendingDeletes: [],
            };
        }
    }, [isEditing, headcountData]);

    useAutoSaveParams({
        pageKey: headcountPageKey,
        initialParams: initialParamsRef.current,
        saveParamsCur: currentSaveParams,
        isBegin: isEditing,
        isChangeValue: true,
    });

    // Handler: Check for auto-save before entering edit mode
    const handleHeadcountEdit = useCallback(() => {
        if (showRestorePrompt(headcountPageKey)) {
            setAutoSaveVisible(true);
        } else {
            HPRef.current?.handleEdit();
        }
    }, [showRestorePrompt, headcountPageKey, HPRef]);

    // Handler: Discard auto-save and enter edit mode
    const handleAutoSaveCancel = useCallback(() => {
        setAutoSaveVisible(false);
        deleteSaveParamsByPageKey(headcountPageKey);
        setPendingDeletes([]); // Clear pending deletes when discarding auto-save
        HPRef.current?.handleEdit();
    }, [deleteSaveParamsByPageKey, headcountPageKey, HPRef]);

    // Handler: Restore from auto-save
    const handleAutoSaveConfirm = useCallback(async () => {
        const saveValue = getSaveParamsValue(headcountPageKey) || {};

        console.log('🔄 [HEADCOUNT AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setAutoSaveVisible(false);

        // Restore pending deletes if they exist
        if (saveValue.pendingDeletes && Array.isArray(saveValue.pendingDeletes)) {
            setPendingDeletes(saveValue.pendingDeletes);
        }

        // 🔥 CRITICAL: Update headcountData with filtered data from auto-save
        if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0) {
            setHeadcountData(saveValue.tableData);
        }

        // 🔥 CRITICAL: Set manual update flag to prevent polling from overwriting restored data
        isManualUpdateRef.current = true;

        // Update edit data state for auto-save tracking
        if (saveValue.tableData && Array.isArray(saveValue.tableData)) {
            setHeadcountEditData(saveValue.tableData);
        }

        // Enter edit mode after state updates
        // Use setTimeout to ensure React finishes updating the component
        setTimeout(() => {
            HPRef.current?.handleEdit();
            // Expand categories after restoring data from auto-save
            // Use the restored data directly to ensure expansion happens
            if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0 && expandAllCategoriesWithChildren) {
                expandAllCategoriesWithChildren(saveValue.tableData);
            }
        }, 100);
    }, [getSaveParamsValue, headcountPageKey, setHeadcountData, HPRef, expandAllCategoriesWithChildren]);

    // Clear auto-save after successful save
    const clearHeadcountAutoSave = useCallback(() => {
        deleteSaveParamsByPageKey(headcountPageKey);
        setPendingDeletes([]);
    }, [deleteSaveParamsByPageKey, headcountPageKey]);

    // Check if there are unsaved changes (for RoutePrompt)
    const hasHeadcountUnsavedChanges = useMemo(() => {
        if (!isEditing) return false;
        
        const initialData = initialParamsRef.current?.tableData;
        if (!initialData || initialData.length === 0) return false;
        
        const currentData = headcountEditData.length > 0 ? headcountEditData : headcountData;
        
        // Compare using JSON stringify
        const hasChanges = JSON.stringify(initialData) !== JSON.stringify(currentData);
        
        return hasChanges || pendingDeletes.length > 0;
    }, [isEditing, headcountEditData, headcountData, pendingDeletes]);

    return {
        // State
        autoSaveVisible,
        headcountEditData,
        pendingDeletes,
        headcountPageKey,
        isManualUpdateRef,
        hasHeadcountUnsavedChanges,

        // State setters (for external updates)
        setHeadcountEditData,
        setPendingDeletes,

        // Handlers
        handleHeadcountEdit,
        handleAutoSaveCancel,
        handleAutoSaveConfirm,
        clearHeadcountAutoSave,
    };
};
